import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Suspense, lazy } from "react";
import { PerformanceMonitor } from "@/components/PerformanceMonitor";
import { SkipToContent, useFocusManagement, useKeyboardNavigation } from "@/components/AccessibilityFeatures";
import Index from "./pages/Index";
import PathwayDecision from "./pages/PathwayDecision";
import Pricing from "./pages/Pricing";
import LowIncomeApproval from "./pages/LowIncomeApproval";
import Privacy from "./pages/Privacy";
import Liability from "./pages/Liability";
import Terms from "./pages/Terms";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import ThankYou from "./pages/ThankYou";
import NotFound from "./pages/NotFound";
import Welcome from "./pages/Welcome";
import Triage from "./pages/Triage";
import FormSelector from "./pages/FormSelector";
import Forms from "./pages/Forms";
import TribunalLocatorPage from "./pages/TribunalLocatorPage";
import ProtectedRoute from "./components/ProtectedRoute";
import HRTOHelp from "./pages/HRTOHelp";
import LTBHelp from "./pages/LTBHelp";
import SmallClaimsCourt from "./pages/SmallClaimsCourt";
import Contact from "./pages/Contact";
import Feedback from "./pages/Feedback";
import HRTOJourney from "./pages/HRTOJourney";
import LTBJourney from "./pages/LTBJourney";
import SmallClaimsJourney from "./pages/SmallClaimsJourney";
import CriminalJourney from "./pages/CriminalJourney";
import FamilyJourney from "./pages/FamilyJourney";
import SuperiorCourtJourney from "./pages/SuperiorCourtJourney";
import PoliceAccountabilityJourney from "./pages/PoliceAccountabilityJourney";
import CASJourney from "./pages/CASJourney";
import LabourBoardJourney from "./pages/LabourBoardJourney";
import ImmigrationJourney from "./pages/ImmigrationJourney";
import AccountabilityJourney from "./pages/AccountabilityJourney";
import ProvincialAccountabilityJourney from "./pages/ProvincialAccountabilityJourney";
import ErrorBoundary from "./components/ErrorBoundary";
import LiveSupportWidget from "./components/LiveSupportWidget";
import Disclaimer from "./pages/Disclaimer";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import WhatWeDo from "./pages/WhatWeDo";
import Partners from "./pages/Partners";
import Team from "./pages/Team";
import Blog from "./pages/Blog";
import BlogList from "./pages/BlogList";
import OntarioSmallClaimsCourt2025 from "./pages/blog/OntarioSmallClaimsCourt2025";
import Scope from "./pages/Scope";
import Accessibility from "./pages/Accessibility";
import PaymentPolicy from "./pages/PaymentPolicy";
import Roadmap from "./pages/Roadmap";
import Troubleshooting from "./pages/Troubleshooting";
import MediaInquiries from "./pages/MediaInquiries";
import GovernmentInquiries from "./pages/GovernmentInquiries";
import LegalUpdates from "./pages/LegalUpdates";
import CourtInformation from "./pages/CourtInformation";
import Explain from "./pages/Explain";
import LegalResources from "./pages/LegalResources";
import Journey from "./pages/Journey";
import CriminalCourtGuide from "./pages/CriminalCourtGuide";
import CriminalCourtMistakes from "./pages/CriminalCourtMistakes";
import DemoJourney from "./pages/DemoJourney";
import TenantRightsGuide from "./pages/TenantRightsGuide";
import FamilyLawGuide from "./pages/FamilyLawGuide";
import HumanRightsGuide from "./pages/HumanRightsGuide";
import LTBT2FormGuide from "./pages/LTBT2FormGuide";

// Lazy load heavy components
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminFormsSync = lazy(() => import("./pages/AdminFormsSync"));
const DocumentAnalysis = lazy(() => import("./pages/DocumentAnalysis"));
const FormBuilder = lazy(() => import("./pages/FormBuilder"));
const CaseAssessment = lazy(() => import("./pages/CaseAssessment"));
const Profile = lazy(() => import("./pages/Profile"));
const Evidence = lazy(() => import("./pages/Evidence"));
const LegalChat = lazy(() => import("./pages/LegalChat"));
const TutorialLibrary = lazy(() => import("./pages/TutorialLibrary"));
const TemplateLibrary = lazy(() => import("./pages/TemplateLibrary"));
const Referrals = lazy(() => import("./pages/Referrals"));
const SmartDocuments = lazy(() => import("./pages/SmartDocuments"));
const CaseTimeline = lazy(() => import("./pages/CaseTimeline"));
const DocumentDrafter = lazy(() => import("./pages/DocumentDrafter"));
const CaseStrengthAnalyzer = lazy(() => import("./pages/CaseStrengthAnalyzer"));
const SettlementCalculator = lazy(() => import("./pages/SettlementCalculator"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const FeatureGuide = lazy(() => import("./pages/FeatureGuide"));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  useFocusManagement();
  useKeyboardNavigation();
  
  return (
    <ErrorBoundary>
      <div className="min-h-screen">
        <PerformanceMonitor />
        <SkipToContent />
        <LiveSupportWidget />
      <Routes>
          {/* Main Landing & Getting Started */}
          <Route path="/" element={<Index />} />
          <Route path="/demo-journey" element={<DemoJourney />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/triage" element={<Triage />} />
          <Route path="/journey" element={<Journey />} />
          <Route path="/pricing" element={<Pricing />} />

          {/* Legal Journey Pathways */}
          <Route path="/hrto-journey" element={<HRTOJourney />} />
          <Route path="/ltb-journey" element={<LTBJourney />} />
          <Route path="/small-claims-journey" element={<SmallClaimsJourney />} />
          <Route path="/criminal-journey" element={<CriminalJourney />} />
          <Route path="/family-journey" element={<FamilyJourney />} />
          <Route path="/superior-court-journey" element={<SuperiorCourtJourney />} />
          <Route path="/cas-journey" element={<CASJourney />} />
          <Route path="/labour-journey" element={<LabourBoardJourney />} />
          <Route path="/immigration-journey" element={<ImmigrationJourney />} />
          <Route path="/accountability-journey" element={<AccountabilityJourney />} />
          <Route path="/accountability/:province/:type" element={<ProvincialAccountabilityJourney />} />
          <Route path="/police-accountability-journey" element={<PoliceAccountabilityJourney />} />

          {/* Tools & Services (Protected) */}
          <Route path="/dashboard" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Profile /></Suspense></ProtectedRoute>} />
          <Route path="/legal-chat" element={<Suspense fallback={<LoadingFallback />}><LegalChat /></Suspense>} />
          <Route path="/document-analysis" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><DocumentAnalysis /></Suspense></ProtectedRoute>} />
          <Route path="/document-drafter" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><DocumentDrafter /></Suspense></ProtectedRoute>} />
          <Route path="/smart-documents" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><SmartDocuments /></Suspense></ProtectedRoute>} />
          <Route path="/evidence" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Evidence /></Suspense></ProtectedRoute>} />
          <Route path="/case-timeline" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><CaseTimeline /></Suspense></ProtectedRoute>} />
          <Route path="/case-strength" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><CaseStrengthAnalyzer /></Suspense></ProtectedRoute>} />
          <Route path="/assessment" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><CaseAssessment /></Suspense></ProtectedRoute>} />
          <Route path="/settlement-calculator" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><SettlementCalculator /></Suspense></ProtectedRoute>} />
          <Route path="/pathway/:caseId" element={<ProtectedRoute><PathwayDecision /></ProtectedRoute>} />

          {/* Forms Management */}
          <Route path="/forms" element={<Forms />} />
          <Route path="/forms/:venue" element={<ProtectedRoute><FormSelector /></ProtectedRoute>} />
          <Route path="/form/:formId" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><FormBuilder /></Suspense></ProtectedRoute>} />

          {/* Resources & Learning */}
          <Route path="/tutorials" element={<Suspense fallback={<LoadingFallback />}><TutorialLibrary /></Suspense>} />
          <Route path="/templates" element={<Suspense fallback={<LoadingFallback />}><TemplateLibrary /></Suspense>} />
          <Route path="/legal-resources" element={<LegalResources />} />
          <Route path="/features" element={<Suspense fallback={<LoadingFallback />}><FeatureGuide /></Suspense>} />
          <Route path="/criminal-court-guide" element={<CriminalCourtGuide />} />
          <Route path="/criminal-court-mistakes" element={<CriminalCourtMistakes />} />
          <Route path="/tribunal-locator" element={<TribunalLocatorPage />} />
          <Route path="/court" element={<CourtInformation />} />
          <Route path="/explain" element={<Explain />} />
          
          {/* SEO Pillar & Cluster Pages */}
          <Route path="/tenant-rights-ontario-guide" element={<TenantRightsGuide />} />
          <Route path="/family-law-ontario-guide" element={<FamilyLawGuide />} />
          <Route path="/human-rights-ontario-guide" element={<HumanRightsGuide />} />
          <Route path="/ltb-t2-form-guide" element={<LTBT2FormGuide />} />

          {/* Company & Support */}
          <Route path="/about" element={<About />} />
          <Route path="/team" element={<Team />} />
          <Route path="/scope" element={<Scope />} />
          <Route path="/what-we-do" element={<WhatWeDo />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/ontario-small-claims-court-2025" element={<OntarioSmallClaimsCourt2025 />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/roadmap" element={<Roadmap />} />
          <Route path="/troubleshooting" element={<Troubleshooting />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/referrals" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Referrals /></Suspense></ProtectedRoute>} />
          <Route path="/media-inquiries" element={<MediaInquiries />} />
          <Route path="/government-inquiries" element={<GovernmentInquiries />} />
          <Route path="/legal-updates" element={<LegalUpdates />} />

          {/* Legal & Policies */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/liability" element={<Liability />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/payment-policy" element={<PaymentPolicy />} />
          <Route path="/accessibility" element={<Accessibility />} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Admin /></Suspense></ProtectedRoute>} />
          <Route path="/admin/forms-sync" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminFormsSync /></Suspense></ProtectedRoute>} />

          {/* Payment & Subscription */}
          <Route path="/low-income" element={<ProtectedRoute><LowIncomeApproval /></ProtectedRoute>} />
          <Route path="/low-income-approval" element={<Navigate to="/low-income" replace />} />
          <Route path="/court-information" element={<Navigate to="/court" replace />} />
          <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/subscription-success" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><SubscriptionSuccess /></Suspense></ProtectedRoute>} />
          <Route path="/payment-cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
          <Route path="/thank-you" element={<ThankYou />} />

          {/* Legacy/Deprecated routes - kept for backwards compatibility */}
          <Route path="/hrto-help" element={<HRTOHelp />} />
          <Route path="/ltb-help" element={<LTBHelp />} />
          <Route path="/small-claims-court" element={<SmallClaimsCourt />} />

          {/* 404 Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
